"use client";

import { Container, Typography, Box, Button, Paper, Alert, useTheme } from "@mui/material";
import Link from "next/link";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import EnrollmentHeader from "@/components/EnrollmentHeader";
import EnrollmentFooter from "@/components/EnrollmentFooter";
import { resolveStepDisabled, useStudyStatus } from "@/hooks/useStudyStatus";
import { useMockDate } from "@/hooks/useMockDate";
import { usePageTitle } from "@/hooks/usePageTitle";
import IndicationOfInterestForm from "@/components/IndicationOfInterestForm";

export default function NadaLandingPage() {
    const theme = useTheme();
    const tenant = "nada";

    usePageTitle("Workforce Study Enrollment", tenant);
    const mockDate = useMockDate();
    const { status, studyYear, statusMessage, previousStudy } = useStudyStatus(
        tenant,
        mockDate
    );

    // Whether the closed cycle still takes payroll: an explicit admin override wins, and with no
    // override the cycle's own submission window decides. Both come from the API, so this line
    // disappears on its own once the study stops accepting submissions.
    const payrollStillAccepted = previousStudy
        ? !resolveStepDisabled(
              previousStudy.stepOverrides.upload_payroll,
              !previousStudy.isSubmissionOpen
          )
        : false;

    return (
        <Box sx={{ bgcolor: "white", minHeight: "100vh" }}>
            <EnrollmentHeader tenant={tenant} />

            <Container maxWidth="lg" sx={{ py: 4 }} component="main" role="main">
                {/* The previous cycle: closed, but possibly still taking payroll. */}
                {previousStudy && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 3 }}
                        role="alert"
                        aria-live="polite"
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {previousStudy.statusMessage}
                        </Typography>
                        {payrollStillAccepted && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Payroll submission is still accepted.
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* Hero Section with CTA */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 4,
                        mb: 3,
                        textAlign: "center",
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                    }}
                    component="section"
                    aria-labelledby="hero-title"
                >
                    <Typography
                        id="hero-title"
                        variant="h4"
                        component="h1"
                        sx={{ mb: 2, fontWeight: 700 }}
                    >
                        Welcome to the NADA Dealership Workforce Study
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
                        The most comprehensive study on dealership workforce trends.
                    </Typography>
                    <Button
                        component={Link}
                        href="/portal"
                        variant="contained"
                        size="large"
                        aria-label="Go to dealer portal"
                        sx={{
                            bgcolor: "white",
                            color: theme.palette.primary.main,
                            px: 6,
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.9)",
                            },
                        }}
                    >
                        Go to Dealer Portal
                    </Button>
                </Paper>

                {/* Indication of Interest Form - Show when enrollment not yet open */}
                {status === "EnrollmentNotYetOpen" && (
                    <Box sx={{ mb: 3 }}>
                        <IndicationOfInterestForm tenant={tenant} studyYear={studyYear} />
                    </Box>
                )}

                {/* Benefits Section - Compact Grid */}
                <Paper
                    elevation={2}
                    sx={{ p: 3, mb: 3 }}
                    component="section"
                    aria-labelledby="benefits-title"
                >
                    <Typography
                        id="benefits-title"
                        variant="h5"
                        component="h2"
                        sx={{ mb: 2, fontWeight: 700 }}
                    >
                        Why Participate?
                    </Typography>
                    <Box
                        role="list"
                        aria-label="Key benefits"
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                        >
                            <DashboardIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 28,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Box>
                                <Typography variant="subtitle2">
                                    Make comparisons to peer groups
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Gain insights into retention strategies
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                            <AssessmentIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 24,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                Make informed decisions on pay plans
                            </Typography>
                        </Box>
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                            <GroupsIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 24,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                Reduce turnover and retain top employees
                            </Typography>
                        </Box>
                        <Box
                            role="listitem"
                            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                        >
                            <InsightsIcon
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: 24,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                Understand demographic challenges
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* What do I receive for participating */}
                <Paper
                    elevation={2}
                    sx={{ p: 3, mb: 3 }}
                    component="section"
                    aria-labelledby="how-it-works-title"
                >
                    <Typography
                        id="how-it-works-title"
                        variant="h6"
                        component="h2"
                        sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                        What do I receive for participating?
                    </Typography>
                    <Typography variant="h8" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Workforce Dashboard: Your data at your fingertips
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        View your submitted data and conduct comparison analysis using custom
                        filters online and on demand. Exclusive one-year access for study
                        participants only.
                    </Typography>
                    <Box>
                        <Button
                            component="a"
                            href="https://player.vimeo.com/video/1143921518"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="large"
                            sx={{ mb: 3, width: "400px" }}
                        >
                            Watch Workforce Dashboard Demo
                        </Button>
                    </Box>

                    <Typography variant="h8" sx={{ fontWeight: 600 }}>
                        2026 NADA National & Regional Trends Report (2025 Data)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        National & regional summary and analysis of auto dealership employee
                        compensation, benefits, demographics, retention and hiring trends.{" "}
                    </Typography>
                    <Box>
                        <Button
                            component="a"
                            href="/files/2026-nada-sample-report.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="large"
                            sx={{ width: "400px" }}
                        >
                            View Sample Report (PDF)
                        </Button>
                    </Box>
                </Paper>

                {/* How It Works */}
                <Paper
                    elevation={2}
                    sx={{ p: 3, mb: 3 }}
                    component="section"
                    aria-labelledby="how-it-works-title"
                >
                    <Typography
                        id="how-it-works-title"
                        variant="h6"
                        component="h2"
                        sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                        How It Works
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Once enrolled, you&apos;ll receive an email with instructions to complete an
                        online benefits questionnaire and payroll file. All responses and data are
                        confidential.
                    </Typography>
                </Paper>

                {/* Deadline Alert — the served cycle, e.g. "Enrollment opens December 01, 2026". */}
                {statusMessage && (
                    <Alert
                        severity="info"
                        icon={false}
                        sx={{ textAlign: "center" }}
                        role="alert"
                        aria-live="polite"
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {statusMessage}
                        </Typography>
                    </Alert>
                )}

                <EnrollmentFooter tenant={tenant} />
            </Container>
        </Box>
    );
}
